/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useEffect } from 'react';

// Mantine
import { Title, Group, Box, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';

// Components
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';
import { TextInput } from '@components/atoms/TextInput';
import { Link } from '@components/atoms/Link';
import { PasswordInput } from '@components/atoms/PasswordInput/PasswordInput';

// Styles
import classes from './ResetPassword.module.css';

// Fetch data
import { useResetPassword } from '@features/update-password/api/reset-password';

// I18n
import { useTranslation } from 'react-i18next';

// Router
import { useLocation, useNavigate } from 'react-router';

// Types
import { ResetPasswordData } from '@features/update-password/types';

// Icons
import { IconInfoCircle } from '@tabler/icons-react';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type ResetPasswordProps = {
	email: string;
	token: string;
	isValidToken: boolean;
	isTokenExpired: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ResetPassword = (
	props: ResetPasswordProps
) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const icon = <IconInfoCircle />;

	const form = useForm({
		initialValues: {
			email: props.email,
			password: '',
			confirmPassword: '',
		},
		validate: {
			password: (value) =>
				value.length < 8
					? 'form.error.passwordMustHaveEightLetters'
					: null,
			confirmPassword: (value, values) =>
				value !== values.password
					? 'form.error.passwordsDidNotMatch'
					: null,
		},
	});

	const resetPassword = useResetPassword();

	useEffect(() => {
		if (resetPassword.isSuccess) {
			form.reset();
		}
	}, [resetPassword.isSuccess]);

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit((values) => {
				const payload: ResetPasswordData = {
					password: values.password,
					token: props.token ? props.token : '',
				};
				resetPassword.mutate(payload);
			})}
		>
			<Box className={classes.containerForm}>
				{location.pathname == '/auth/sign_in' ? (
					<Box>
						<Title hiddenFrom="xxl" size="h4">
							{t('resetPassword.choosePassword')}
						</Title>
						<Title visibleFrom="xxl" size="h3">
							{t('resetPassword.choosePassword')}
						</Title>
					</Box>
				) : (
					<Box>
						<Title hiddenFrom="xxl" size="h4">
							{t('resetPassword.title')}
						</Title>
						<Title visibleFrom="xxl" size="h3">
							{t('resetPassword.title')}
						</Title>
					</Box>
				)}

				<Text
					hiddenFrom="xxl"
					type="body2"
					color="inputs-texts-icons"
				>
					{t('resetPassword.description')}
				</Text>
				<Text
					visibleFrom="xxl"
					type="body1"
					color="inputs-texts-icons"
				>
					{t('resetPassword.description')}
				</Text>
				<Box className={classes.spacerVertical32} />

				{props.isValidToken ? (
					!props.isTokenExpired ? (
						<>
							<TextInput
								disabled
								size="lg"
								hiddenFrom="xxl"
								radius="8.29px"
								placeholder={t('auth.yourEmailAddress')}
								{...form.getInputProps('email')}
							/>
							<TextInput
								disabled
								size="xl"
								visibleFrom="xxl"
								radius="8.29px"
								placeholder={t('auth.yourEmailAddress')}
								{...form.getInputProps('email')}
							/>
							<Box className={classes.spacerVertical24} />
							<PasswordInput
								hiddenFrom="xxl"
								size="lg"
								radius="8.29px"
								placeholder={t(
									'resetPassword.newPassword'
								)}
								{...form.getInputProps('password')}
							/>
							<PasswordInput
								visibleFrom="xxl"
								size="xl"
								radius="8.29px"
								placeholder={t(
									'resetPassword.newPassword'
								)}
								{...form.getInputProps('password')}
							/>
							<Box className={classes.spacerVertical24} />
							<PasswordInput
								hiddenFrom="xxl"
								size="lg"
								radius="8.29px"
								placeholder={t(
									'resetPassword.confirmNewPassword'
								)}
								{...form.getInputProps('confirmPassword')}
							/>
							<PasswordInput
								visibleFrom="xxl"
								size="xl"
								radius="8.29px"
								placeholder={t(
									'resetPassword.confirmNewPassword'
								)}
								{...form.getInputProps('confirmPassword')}
							/>
							<Box
								className={
									classes.containerForgetPassword
								}
							></Box>
							<Button
								visibleFrom="xxl"
								size="lg"
								fullWidth
								type="submit"
								loading={
									resetPassword && resetPassword.isLoading
								}
							>
								{t('global.submit')}
							</Button>
							<Button
								hiddenFrom="xxl"
								size="md"
								fullWidth
								type="submit"
								loading={
									resetPassword && resetPassword.isLoading
								}
							>
								{t('global.submit')}
							</Button>
							<Group justify="center" mt={10}>
								<Link
									onClick={() => {
										navigate('/login');
									}}
									color="blue"
									size="h6"
								>
									{t('global.return')}
								</Link>
							</Group>
						</>
					) : (
						<Alert
							variant="light"
							color="error"
							title={t(
								'resetPassword.passwordResetExpiredLinkIssue'
							)}
							icon={icon}
						>
							{t(
								'resetPassword.passwordResetExpiredLinkIssueDescription'
							)}
						</Alert>
					)
				) : (
					<Alert
						variant="light"
						color="error"
						title={t(
							'resetPassword.passwordResetLinkIssue'
						)}
						icon={icon}
					>
						{t(
							'resetPassword.passwordResetLinkIncorrect'
						)}
					</Alert>
				)}
			</Box>
		</form>
	);
};
